import { appGetState } from 'legacy/lut/store'

import { getTokens } from 'core/classes/helpers/StorageHelper'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { HttpCancel, HttpHeaders, Rest } from 'core/classes/http'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import settings from 'src/config/settings'

import {
  TPartnerPlanExportFormats,
  TPartnerPlanPdfVersion,
} from '../../partner360/ExportCommitmentService'

import { IPartnerPlanRequest, IPartnerPlanResponse } from './IPartnerPlan'

export class Partner360Api {
  public static generatePartnerPlan = async (
    partnerId: string,
    fyear: number,
    format: TPartnerPlanExportFormats,
    programType?: string,
    pdfVersion?: TPartnerPlanPdfVersion
  ): Promise<IPartnerPlanResponse> => {
    try {
      // IGeneratePartnerPlanRequest
      const pdfVersionParam = pdfVersion ? `&pdfVersion=${pdfVersion}` : ''
      const endpoint = `/api/v0/partnerPlan/generate?partnerId=${partnerId}&fyear=${fyear}&programType=${programType}&format=${format.toLowerCase()}${pdfVersionParam}`
      const url = `${settings.httpsServices.partner360}${endpoint}`
      const [accessToken] = await getTokens(appGetState().app.ownerId)

      const init: RequestInit = {
        headers: {
          Authorization: `${accessToken}`,
        },
        method: 'GET',
      }
      const response = await fetch(url, init)
      const apiResponse = (await response.json()) as IPartnerPlanResponse

      return apiResponse
    } catch (e) {
      return {
        status: 'error',
        data: undefined,
        message: (e || '').toString(),
      }
    }
  }

  public static generatePartnerPlanWs = async (
    partnerId: string,
    fyear: number,
    programType?: string
  ): Promise<IPartnerPlanResponse> => {
    try {
      const wsClient = WebSocketStore.get('partner360')
      const [token] = await getTokens(appGetState().app.ownerId)
      const payload: IPartnerPlanRequest = {
        partnerId: partnerId,
        fyear: fyear,
        programType: programType,
      }

      const isSecure =
        !!settings.partner360Service?.wsServer &&
        settings.partner360Service.wsServer.startsWith('wss')

      const apiResponse = await wsClient.call<IPartnerPlanResponse>({
        service: 'partnerPlan',
        action: 'generate',
        payload,
        token: token || undefined,
        returnError: true,
        withoutTokenRefresh: true,
        isAuth: isSecure,
      })

      return apiResponse
    } catch (e) {
      return {
        status: 'error',
        data: undefined,
        message: (e || '').toString(),
      }
    }
  }

  static callSPJBPMasterDealerEP = async (spName: string, partnerId: string) => {
    //metrics.loadDataStart(spName)

    const url = proxy(`/webservice/740/sp/v3/${spName}/call`)
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const payload: IObjectAny = {
      'p.PartnerId': partnerId,
    }

    const response = await Rest.post({ url, payload, cancelObject: new HttpCancel(), headers })
    //const isSuccess = response && response['Total'] === 1
    //metrics.loadDataStop(spName)

    return response
  }

  // Call example: await Partner360Api.changePartnerFlags(el.ExtendedPartnerId, 'Program',
  // el.ExtendedPartnerRegion, new Date(), new Date(2049, 11, 31), 'Add' | 'Remove', 'Partner 360')
  static changePartnerFlags = async (
    partnerId: string,
    codeType: string,
    region: string,
    startDate: Date,
    endDate: Date,
    action: string,
    source: string
  ) => {
    const url = proxy('/webservice/630/sp/v3/CnGTriggerPartnerFlagChange/call')
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const codes = Partner360Api.getCodeByRegion(region)
    const payload = {
      'p.PartnerId': partnerId,
      'p.CodeType': codeType,
      'p.Code': codes.code,
      'p.CodeName': codes.codeName,
      'p.StartDate': startDate,
      'p.EndDate': endDate,
      'p.Action': action,
      'p.Source': source,
    }

    const response = await Rest.post({ url, payload, cancelObject: new HttpCancel(), headers })
    return response['Rows']
  }

  static getCodeByRegion = (region: string) => {
    switch (region) {
      case 'APJ':
        return {
          codeName: 'TC Acceptance MPS Master Dealer Extended APJ',
          code: 'TC_APJ_MPS_MD_EXT',
        }
      case 'AMS':
        return {
          codeName: 'TC Acceptance MPS Master Dealer Extended AMS',
          code: 'TC_AMS_MPS_MD_EXT',
        }
      default:
        return {
          codeName: 'TC Acceptance MPS Master Dealer Extended EMEA',
          code: 'TC_EMEA_MPS_MD_EXT',
        }
    }
  }
}
